import MapboxDraw, { DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import { Feature, GeoJsonProperties, LineString, Point } from "geojson";
import rbush from "geojson-rbush";
import { uniqBy } from "lodash";

import { DRAWING_CONSTANTS } from "fond/constants";

import directSelectMode from "../../base/directSelect";

import { isValid, pullInPaths, pullInPoints } from "./helper";

const {
  lib: {
    CommonSelectors: { isInactiveFeature, isFeature },
  },
} = MapboxDraw;

interface DrawCustomModeState {
  map: mapboxgl.Map;
  paths: Feature<LineString>[];
  points: Feature<Point>[];
}

const directSelect: DrawCustomMode<DrawCustomModeState> = {
  ...directSelectMode,

  /**
   * When a feature is first selected we store within state the currently connected
   * ug_paths & ug_points so that we can re-validate all associated features on mouse up.
   */
  onMouseDown: function (state, event) {
    if (isInactiveFeature(event)) {
      const allPaths: Feature<LineString>[] = [];
      const allPoints: Feature<Point>[] = [];
      const selectedFeatures = this.getSelected().map((feature) => feature.toGeoJSON());

      // Get all colocated features for existing selections & the new feature being selected.
      [...selectedFeatures, event.featureTarget].forEach((feature) => {
        if (feature.geometry.type === "Point") allPaths.push(...pullInPaths.call(this, feature, this._ctx.options.modeOptions.pathLayerId));
        if (feature.geometry.type === "LineString") allPoints.push(...pullInPoints.call(this, feature, this._ctx.options.modeOptions.pointLayerId));
      });

      // Update the state to include the colocated features.
      state.paths = uniqBy(allPaths, "properties.id");
      state.points = uniqBy(allPoints, "properties.id");
    }

    // Reset the colocated features
    if (!isFeature(event)) {
      state.paths = [];
      state.points = [];
    }

    directSelectMode.onMouseDown?.call(this, state, event);
  },

  /**
   * On mouse up validate all the currently selected features that have been moved &
   * re-render any collocated features to re-validate.
   */
  onMouseUp: function (state, event) {
    const tree = rbush();
    tree.load(this._ctx.api.getAll());
    const allFeaturesToRefresh: Feature<LineString | Point, GeoJsonProperties>[] = [...state.paths, ...state.points];

    this._ctx.api.getSelected().features.forEach((feature) => {
      const collocatedFeatures = tree.search(feature).features as Feature<LineString | Point>[];
      allFeaturesToRefresh.push(...collocatedFeatures);
    });

    // Re-render all collocated features
    allFeaturesToRefresh.forEach((f) => {
      if (f.id) this._ctx.api.setFeatureProperty(f.id, DRAWING_CONSTANTS.Valid, isValid(f, tree, this._ctx.options.modeOptions).toString());
      this.doRender(f.id);
    });

    directSelectMode.onMouseUp?.call(this, state, event);
  },
};

export default directSelect;
